import { useAuth } from '@shared/components/AuthProvider';
import { useTranslation } from '@shared/core';
import { useFeatureValue } from '@shared/core/featureFlags';

type BookmarkletFunctionParams = Readonly<{
  options: JoyBookMarkletOptions;
  scriptUrl: string;
  scriptLoadError: string;
}>;

// this function needs to have zero dependencies.
// so we keep it in the module scope to avoid accidentally
// using some code from elsewhere
const loadBookmarkletFunc = ({ options, scriptUrl, scriptLoadError }: BookmarkletFunctionParams) => {
  const global = window;
  const document = global.document;

  global.joyBookMarklet = global.joyBookMarklet || {};
  const joyBookMarklet = global.joyBookMarklet;
  joyBookMarklet.options = options;

  if (typeof joyBookMarklet.init === 'function') {
    joyBookMarklet.init();
  } else {
    const scriptElem = document.createElement('script');
    let testScriptUrl = '';

    try {
      testScriptUrl = global.localStorage.joyBookMarkletScriptUrl;
    } catch (e) {}

    scriptElem.setAttribute('src', testScriptUrl || scriptUrl);
    scriptElem.addEventListener('error', () => {
      global.alert(scriptLoadError);
    });
    document.body.appendChild(scriptElem);
  }
};

export const useBookmarkletController = () => {
  const { t2 } = useTranslation('bookmarklet');
  const browserButtonText = t2('browserButton');
  const {
    loginManager: { tokenService },
  } = useAuth();

  const usePopup = useFeatureValue('bookmarkletPopup');

  const getBookmarklet = () => {
    const global = window;
    const location = global.location;
    const queryString = location.search;
    const path = location.pathname;
    const origin = location.origin;
    const eventHandle = path.split('/')[1];

    const isLocal = origin === 'http://localhost:3000' || origin === 'http://0.0.0.0:3000';
    // testing on local has a different url for bookmarklet
    const scriptUrl = isLocal ? `${origin}/bliss-apps/joy-web/web/content/js/registry-bookmarklet.js` : `${origin}/bookmarklet-snippet${queryString}`;

    const functionBodyEncoded = global.encodeURIComponent(loadBookmarkletFunc.toString());

    const args: BookmarkletFunctionParams = {
      options: {
        eventHandle,
        origin,
        queryString,
        usePopup,
        source: 'button',
        hash: tokenService.getSecretsForHashBasedLogin(),
        version: 2,
      },
      scriptUrl,
      scriptLoadError: browserButtonText.scriptLoadError,
    };

    return {
      script: `javascript:(${functionBodyEncoded})(${global.encodeURIComponent(JSON.stringify(args))});`,
      usePopup,
      isLocal,
    } as const;
  };

  return {
    getBookmarklet,
  };
};
